import {
  Tabs as MantineTabs,
  TabsProps as MantineTabsProps,
  TabsTabProps as MantineTabsTabProps,
  useTabsContext,
} from '@mantine/core';
import { ForwardedRef, forwardRef } from 'react';

import { Counter } from '../Counter';
import { Tooltip } from '../Tooltip';
import styles from './TabButtonGroup.module.css';

export type TabButtonGroupSize = 'sm' | 'md' | 'lg';
export type TabButtonGroupType = 'text' | 'iconOnly';
export type TabButtonGroupVariant = 'default' | 'ai' | 'borderless';

type TabButtonGroupBaseProps = Omit<
  MantineTabsProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'radius'
  | 'size'
  | 'type'
> & {
  /**
   * Visual treatment of the selected segment. `'ai'` renders Figma's
   * `AI Selected` state — the purple gradient fill — instead of the flat
   * indigo one. Only the active segment differs; Default, Hover and Disabled
   * are identical between `'default'` and `'ai'`.
   *
   * `'borderless'` renders Figma's `Tabs Mobile` layout instead: the shared
   * container border, fill and padding are dropped and each segment becomes a
   * standalone Secondary button — its own border, a 16px gap, and a selected
   * state that keeps the label at default primary rather than indigo.
   *
   * Default: `'default'`.
   */
  variant?: TabButtonGroupVariant;
  /**
   * @deprecated Not part of the documented Figma spec. Kept for existing
   * consumers only.
   */
  isFullWidth?: boolean;
};

/**
 * `size='sm'` exists only for icon-only groups — there is no small text
 * segment in the design system, so the two axes are modelled as a union.
 */
export type TabButtonGroupProps = TabButtonGroupBaseProps &
  (
    | {
        /** Segment style for the whole group. Default: `'text'`. */
        type?: 'text';
        /** Group height. Default: `'lg'`. `'sm'` requires `type='iconOnly'`. */
        size?: Exclude<TabButtonGroupSize, 'sm'>;
      }
    | {
        /** Segment style for the whole group. Default: `'text'`. */
        type: 'iconOnly';
        /** Group height. Default: `'lg'`. */
        size?: TabButtonGroupSize;
      }
  );

const classNames = {
  root: styles.root,
  list: styles.list,
  tab: styles.tab,
  tabLabel: styles.tabLabel,
  tabSection: styles.tabSection,
};

const TabButtonGroupBase = forwardRef(
  (
    {
      value,
      onChange,
      onBlur,
      size = 'lg',
      type = 'text',
      variant = 'default',
      isFullWidth = false,
      ...props
    }: TabButtonGroupProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    /*
     * INFO: We trigger onBlur in the onChange event here because it can be confusing that
     * a click outside the component is required to get rid of validation errors
     */
    const handleTabChange = (newValue: string | null) => {
      onChange?.(newValue);
      /*
       * INFO: onTabChange() from Mantine doesn't pass on event arg which onBlur() wants,
       * but blurring works correctly here without args as well.
       * onBlur is destructured out of props so it does NOT get spread onto MantineTabs —
       * that would cause it to fire natively on every focus-move between buttons AND here,
       * resulting in double-fire. Calling it once here is the V1 contract.
       */
      // @ts-expect-error Expected 1 arguments, but got 0.
      onBlur?.();
    };

    return (
      <MantineTabs
        ref={ref}
        unstyled
        variant="none"
        value={value}
        classNames={classNames}
        onChange={handleTabChange}
        data-input-size={size}
        data-type={type}
        data-variant={variant}
        data-full-width={isFullWidth || undefined}
        {...props}
      />
    );
  }
);

TabButtonGroupBase.displayName = 'ZorroTabButtonGroup';

export interface TabButtonGroupTabProps extends Omit<
  MantineTabsTabProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style'
> {
  /**
   * Renders a `Counter` between the label and the right section.
   * Omitted or `0` renders nothing (per Figma: hide rather than render 0).
   */
  counter?: number;
  /**
   * Noun used in the tab's accessible name, e.g. "All plans, 102 results".
   * Default: `'results'`.
   */
  counterLabel?: string;
  /**
   * Wraps the segment in a Tooltip and, unless `aria-label` is given, supplies
   * the accessible name. Required in practice for `type='iconOnly'` groups,
   * which have no visible label.
   */
  tooltip?: string;
}

const TabButtonGroupTab = forwardRef<HTMLButtonElement, TabButtonGroupTabProps>(
  (
    {
      counter,
      counterLabel = 'results',
      tooltip,
      children,
      disabled,
      value,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    const { value: activeValue } = useTabsContext();
    const isActive = activeValue === value;
    const showCounter = counter !== undefined && counter !== 0;

    const tab = (
      <MantineTabs.Tab
        ref={ref}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel ?? tooltip}
        {...props}
      >
        {children}
        {showCounter && (
          <>
            {/*
             * The count belongs in the tab's accessible name, not as a separate
             * element — so the visible Counter is hidden from AT and the text
             * is appended to the label instead.
             */}
            <span
              className={styles.visuallyHidden}
            >{`, ${counter} ${counterLabel}`}</span>
            <span className={styles.counterSlot} aria-hidden="true">
              {/*
               * aria-hidden lives on this wrapper because CounterProps is a
               * closed interface with no rest spread.
               */}
              <Counter
                number={counter}
                size="sm"
                type={isActive ? 'on-color' : 'default'}
                state={disabled ? 'empty' : 'default'}
              />
            </span>
          </>
        )}
      </MantineTabs.Tab>
    );

    /*
     * Mantine's Tooltip clones its child and renders no wrapper DOM, so
     * role="tab" stays a direct child of role="tablist". This only works
     * because the tab above forwards its ref.
     */
    return tooltip ? <Tooltip label={tooltip}>{tab}</Tooltip> : tab;
  }
);

TabButtonGroupTab.displayName = 'ZorroTabButtonGroupTab';

export const TabButtonGroup = Object.assign(TabButtonGroupBase, {
  Tab: TabButtonGroupTab,
  List: MantineTabs.List,
  Panel: MantineTabs.Panel,
});
